/**
 * Per-channel presence state machine (SOC-5785 / realtime layer ADR).
 *
 * Single source of truth for "is this uuid connected" — every surface
 * (results grid, header student list) reads the same store, so they can
 * never disagree. Framework-free and timer-injectable so the flap-grace
 * behavior is testable without React.
 *
 * Semantics:
 * - join → present immediately (and cancels any pending disconnect)
 * - leave/timeout → absent only after a grace window with no contradicting
 *   signal, because school Wi-Fi blips surface as leave+join pairs
 * - snapshot (hereNow) → authoritative replace; also marks the store
 *   "seeded" so absence becomes a real answer instead of unknown
 */

import { realtimeLog } from './debug';

export interface PresenceEntry {
  isPresent: boolean;
  lastSeen: number;
}

type Listener = () => void;

const DEFAULT_DISCONNECT_GRACE_MS = 5000;

const defaultGraceMs = () => {
  const configured = window.APP_CONFIG?.PRESENCE_DISCONNECT_GRACE_MS;
  if (typeof configured === 'number' && configured >= 0) {
    return configured;
  }
  return DEFAULT_DISCONNECT_GRACE_MS;
};

export class PresenceStore {
  private entries = new Map<string, PresenceEntry>();
  private pendingDisconnects = new Map<string, ReturnType<typeof setTimeout>>();
  private listeners = new Set<Listener>();
  private seeded = false;
  private snapshotCache: ReadonlyMap<string, PresenceEntry> | null = null;

  private graceMs: () => number;
  private now: () => number;
  private label: string;

  constructor(
    opts: { graceMs?: () => number; now?: () => number; label?: string } = {}
  ) {
    this.graceMs = opts.graceMs ?? defaultGraceMs;
    this.now = opts.now ?? Date.now;
    this.label = opts.label ?? 'presence';
  }

  applyJoin(uuid: string) {
    this.cancelPending(uuid);
    this.setPresent(uuid, true);
  }

  applyLeave(uuid: string) {
    this.scheduleDisconnect(uuid);
  }

  applyTimeout(uuid: string) {
    this.scheduleDisconnect(uuid);
  }

  /**
   * Authoritative occupancy from hereNow. Everyone listed is present;
   * everyone previously known but absent from the list is disconnected.
   * Cancels pending grace timers — the snapshot already is the truth.
   */
  applySnapshot(uuids: string[]) {
    realtimeLog(
      `${this.label}: snapshot applied — ${uuids.length} present`,
      uuids
    );
    this.seeded = true;
    this.pendingDisconnects.forEach((timer) => clearTimeout(timer));
    this.pendingDisconnects.clear();

    const present = new Set(uuids);
    const at = this.now();

    present.forEach((uuid) => {
      this.entries.set(uuid, { isPresent: true, lastSeen: at });
    });
    this.entries.forEach((entry, uuid) => {
      if (!present.has(uuid) && entry.isPresent) {
        this.entries.set(uuid, { isPresent: false, lastSeen: entry.lastSeen });
      }
    });

    this.emit();
  }

  /**
   * Non-presence proof of life (e.g. a student response arrived) — cancels
   * a pending disconnect so a mid-submit Wi-Fi blip never shows as
   * disconnected. Only refreshes a uuid that is present right now:
   * - It never creates one. PubNub would never send a leave for a uuid it
   *   didn't report, so it would stay present forever.
   * - It never revives one whose grace window ran out. PubNub has already
   *   sent that leave, so a revived entry would never clear either. A
   *   student who reconnects comes back with their next join or hereNow.
   */
  markAlive(uuid: string) {
    if (!this.entries.get(uuid)?.isPresent) {
      return;
    }
    this.cancelPending(uuid);
    this.setPresent(uuid, true);
  }

  /**
   * true/false once known; undefined while the store has neither seen an
   * event for this uuid nor been seeded — callers fall back to legacy
   * state during migration.
   */
  getPresence(uuid: string): boolean | undefined {
    const entry = this.entries.get(uuid);
    if (entry) {
      return entry.isPresent;
    }
    return this.seeded ? false : undefined;
  }

  /** Stable reference until the next change — safe for React snapshots. */
  snapshot(): ReadonlyMap<string, PresenceEntry> {
    if (!this.snapshotCache) {
      this.snapshotCache = new Map(this.entries);
    }
    return this.snapshotCache;
  }

  isSeeded(): boolean {
    return this.seeded;
  }

  /** True while any subscriber is attached — the bridge keeps such stores. */
  hasListeners(): boolean {
    return this.listeners.size > 0;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose() {
    this.pendingDisconnects.forEach((timer) => clearTimeout(timer));
    this.pendingDisconnects.clear();
    this.listeners.clear();
    this.entries.clear();
    this.snapshotCache = null;
  }

  private setPresent(uuid: string, isPresent: boolean) {
    const previous = this.entries.get(uuid);
    if (previous?.isPresent === isPresent) {
      // Refresh lastSeen without notifying — presence didn't change. The
      // snapshot cache is deliberately NOT invalidated here: nobody was
      // told anything changed, so a consumer polling snapshot() per render
      // must keep getting the same reference. lastSeen catches up in the
      // snapshot on the next real change.
      this.entries.set(uuid, { isPresent, lastSeen: this.now() });
      return;
    }
    realtimeLog(
      `${this.label}: ${uuid} → ${isPresent ? 'connected' : 'DISCONNECTED'}`
    );
    this.entries.set(uuid, { isPresent, lastSeen: this.now() });
    this.emit();
  }

  private scheduleDisconnect(uuid: string) {
    this.cancelPending(uuid);

    const graceMs = this.graceMs();
    if (graceMs === 0) {
      this.setPresent(uuid, false);
      return;
    }

    realtimeLog(`${this.label}: ${uuid} disconnect pending in ${graceMs}ms`);
    this.pendingDisconnects.set(
      uuid,
      setTimeout(() => {
        this.pendingDisconnects.delete(uuid);
        this.setPresent(uuid, false);
      }, graceMs)
    );
  }

  private cancelPending(uuid: string) {
    const pending = this.pendingDisconnects.get(uuid);
    if (pending) {
      realtimeLog(
        `${this.label}: ${uuid} pending disconnect cancelled (sign of life)`
      );
      clearTimeout(pending);
      this.pendingDisconnects.delete(uuid);
    }
  }

  private emit() {
    this.snapshotCache = null;
    this.listeners.forEach((listener) => listener());
  }
}
